import { useCallback, useEffect, useState } from 'react';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { Mono } from '../ui/Mono';
import { useToast } from '../ui/Toast';
import api from '../../services/api';
import { BeforeAfter, EVENT_LABEL } from './campaignCardShared';

/**
 * AUDITORIA de campanhas (pedido do dono, 20/08). Duas leituras:
 *
 * 1. MUDANÇAS & DIVERGÊNCIAS — a linha do tempo que o sync grava a cada
 *    transição de campanha por anúncio, com o ANTES e o DEPOIS lado a lado. O
 *    destaque é a DIVERGÊNCIA DE OPT-IN: o anúncio ativou numa campanha com
 *    rebate/preço diferente do que estava registrado quando era candidato —
 *    "vi 15%, aplicou 12%". É a garantia que ele pediu: *"comparar o atual
 *    aplicado contra o antigo registrado para garantir que meu opt-in seja
 *    considerado o valor que estavamos vendo"*.
 *
 * 2. BÔNUS (CASHBACK) — as campanhas em que o ML PAGA para vender (sem mexer em
 *    preço nem rebate). Vem do Release Report do MP; o estorno
 *    (`cashback_cancel`) é o ML tomando o bônus de volta quando a venda
 *    cancela. ⚠️ O extrato NÃO referencia a venda (medido) — o vínculo
 *    bônus→venda é fase 2, via consulta ao pagamento no MP.
 */
const FormatDateTime = (value) => (value ? new Date(value).toLocaleString('pt-BR') : '—');
const FormatBrlLike = (value) =>
    value === null || value === undefined
        ? '—'
        : Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const FormatPct = (value) => (value === null || value === undefined ? '—' : `${Number(value)}%`);

// Variantes do <Badge> desta aba (a pílula do card usa EVENT_TONE, do shared).
const EVENT_BADGE = { ENTERED: 'success', CHANGED: 'warning', LEFT: 'secondary' };

export const CampaignItemEventsPanel = () => {
    const toast = useToast();
    const [items, setItems] = useState([]);
    const [onlyDivergent, setOnlyDivergent] = useState(false);
    const [itemFilter, setItemFilter] = useState('');
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await api.getCampaignItemEvents({
                only_divergent: onlyDivergent ? 'true' : undefined,
                item_id: itemFilter.trim() || undefined,
                limit: 100,
            });
            setItems(data?.items || []);
        } catch (error) {
            toast.error(error?.message || 'Erro ao carregar o histórico');
        } finally {
            setLoading(false);
        }
    }, [onlyDivergent, itemFilter, toast]);

    useEffect(() => {
        load();
    }, [load]);

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-end gap-3">
                <Input
                    label="Anúncio (MLB...)"
                    value={itemFilter}
                    onChange={(event) => setItemFilter(event.target.value.toUpperCase())}
                    placeholder="todos"
                    className="w-56 font-mono"
                />
                <Button
                    variant={onlyDivergent ? 'primary' : 'secondary'}
                    onClick={() => setOnlyDivergent((current) => !current)}
                    title="Só ativações em que o aplicado ficou diferente do que estava registrado"
                >
                    {onlyDivergent ? '⚠ Só divergências' : 'Todas as mudanças'}
                </Button>
            </div>

            {loading ? (
                <p className="text-sm text-mono-500">Carregando…</p>
            ) : items.length === 0 ? (
                <p className="text-sm text-mono-500">
                    {onlyDivergent
                        ? 'Nenhuma divergência de opt-in registrada — os valores aplicados bateram com os vistos.'
                        : 'Nenhuma mudança registrada ainda. O histórico começa a existir a partir do próximo sync.'}
                </p>
            ) : (
                <div className="space-y-2">
                    {items.map((row) => (
                        <div
                            key={row.id}
                            className={`rounded-lg border px-4 py-3 ${
                                row.divergent
                                    ? 'border-red-400 bg-red-50 dark:border-red-700 dark:bg-red-950'
                                    : 'border-mono-200 dark:border-mono-800'
                            }`}
                        >
                            <div className="flex flex-wrap items-center gap-2">
                                <Badge variant={EVENT_BADGE[row.event] || 'secondary'}>
                                    {EVENT_LABEL[row.event] || row.event}
                                </Badge>
                                {row.divergent ? (
                                    <Badge variant="danger">aplicado ≠ visto</Badge>
                                ) : null}
                                <Mono className="text-xs">{row.item_id}</Mono>
                                <span className="truncate text-sm font-medium text-black dark:text-white">
                                    {row.item_title || ''}
                                </span>
                                <span className="ml-auto text-xs text-mono-500">{FormatDateTime(row.detected_at)}</span>
                            </div>
                            <p className="mt-1 text-xs text-mono-600 dark:text-mono-400">
                                {row.campaign_name || row.campaign_id}
                            </p>
                            <div className="mt-2 flex flex-wrap">
                                <BeforeAfter label="status" before={row.before_status} after={row.after_status} />
                                <BeforeAfter
                                    label="rebate ML"
                                    before={row.before_meli_percentage}
                                    after={row.after_meli_percentage}
                                    format={FormatPct}
                                />
                                <BeforeAfter
                                    label="seller"
                                    before={row.before_seller_percentage}
                                    after={row.after_seller_percentage}
                                    format={FormatPct}
                                />
                                <BeforeAfter
                                    label="preço"
                                    before={row.before_price}
                                    after={row.after_price}
                                    format={FormatBrlLike}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export const CampaignBonusesPanel = () => {
    const toast = useToast();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            setData(await api.getCampaignBonuses({ limit: 100 }));
        } catch (error) {
            toast.error(error?.message || 'Erro ao carregar os bônus');
        } finally {
            setLoading(false);
        }
    }, [toast]);

    useEffect(() => {
        load();
    }, [load]);

    const summary = data?.summary;

    return (
        <div className="space-y-5">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="rounded-xl border border-mono-200 p-5 dark:border-mono-800">
                    <p className="text-sm text-mono-500">Bônus recebidos</p>
                    <p className="mt-1 text-2xl font-bold text-black dark:text-white">
                        {summary ? FormatBrlLike(summary.total_received) : '—'}
                    </p>
                </div>
                <div className="rounded-xl border border-mono-200 p-5 dark:border-mono-800">
                    <p className="text-sm text-mono-500">Estornados (venda cancelada)</p>
                    <p className="mt-1 text-2xl font-bold text-black dark:text-white">
                        {summary ? FormatBrlLike(summary.total_reversed) : '—'}
                    </p>
                </div>
                <div className="rounded-xl border border-mono-200 p-5 dark:border-mono-800">
                    <p className="text-sm text-mono-500">Líquido</p>
                    <p className="mt-1 text-2xl font-bold text-black dark:text-white">
                        {summary ? FormatBrlLike(summary.net) : '—'}
                    </p>
                </div>
            </div>

            <p className="text-xs text-mono-500">
                Fonte: linhas <Mono>cashback</Mono>/<Mono>cashback_cancel</Mono> do extrato (Release Report do MP). O
                extrato não referencia a venda — o vínculo bônus→venda é fase futura, via consulta ao pagamento no MP.
            </p>

            {loading ? (
                <p className="text-sm text-mono-500">Carregando…</p>
            ) : (
                <div className="max-h-96 overflow-auto rounded-lg border border-mono-200 dark:border-mono-800">
                    <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-mono-50 dark:bg-mono-900">
                            <tr className="text-left text-xs uppercase tracking-wide text-mono-500">
                                <th className="px-3 py-2">Quando</th>
                                <th className="px-3 py-2">Referência</th>
                                <th className="px-3 py-2">Crédito</th>
                                <th className="px-3 py-2">Estorno</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(data?.items || []).map((row) => (
                                <tr key={row.id} className="border-t border-mono-100 dark:border-mono-800">
                                    <td className="whitespace-nowrap px-3 py-1.5 text-mono-600 dark:text-mono-400">
                                        {FormatDateTime(row.movement_date)}
                                    </td>
                                    <td className="px-3 py-1.5 font-mono text-xs">{row.external_reference}</td>
                                    <td className="px-3 py-1.5">
                                        {Number(row.credit) > 0 ? FormatBrlLike(row.credit) : '—'}
                                    </td>
                                    <td className="px-3 py-1.5 text-red-600 dark:text-red-400">
                                        {Number(row.debit) > 0 ? `− ${FormatBrlLike(row.debit)}` : '—'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};
